import Renderer from './Renderer';
import { Texture } from './Texture';

export class FragmentShader {
	binders: Array<WebGLShader>;
	script: string;
	textures: Array<string>;
	uniforms: Array<string>;

	constructor (script: string, uniforms: Array<string>, textures: Array<string> = []) {
		this.binders = [];
		this.script = script;
		this.uniforms = uniforms;
		this.textures = textures;
	}	
}

export class VertexShader extends FragmentShader {
	attributes: Array<string>;

	constructor (script: string, attributes: Array<string>, uniforms: Array<string>, textures: Array<string> = []) {
		super(script, uniforms, textures);
		this.attributes = attributes;
	}
}

interface shaderPropertyMap {
	[key: string]: number;
}

interface uniformMap {
	[key: string]: WebGLUniformLocation;
}

type renderMethod = (gl: WebGLRenderingContext, vertexBuffer: Uint8Array, byteCount: number, vpMatrix: Array<number>) => void;

export class ShaderProgram {
	// render: renderMethod;
	binder: WebGLProgram;
	lastAttributeIndex: number;
	lastTextures: Array<Texture>;
	attributes: shaderPropertyMap;
	// textures: shaderPropertyMap;
	textures: Array<number>;
	uniforms: uniformMap;

	constructor (gl: WebGLRenderingContext, rendererID: number, vertexShader: VertexShader, fragmentShader: FragmentShader/*, render: renderMethod*/) {
		// this.render = render;
		this.attributes = {};
		this.textures = [];
		this.uniforms = {};
		
		// compiling shaders
		const compileShader = (type: number, shader: FragmentShader) => {
			const shaderBinders = shader.binders;

			let shaderBinder = shaderBinders[rendererID];
			if (shaderBinder === undefined) {
				const newShaderBinder = gl.createShader(type);
				if (newShaderBinder === null) {
					throw new Error('Could not create shader.');
				}

				shaderBinders[rendererID] = shaderBinder = newShaderBinder;
			
				gl.shaderSource(newShaderBinder, shader.script);
				gl.compileShader(newShaderBinder);
			
				if (!gl.getShaderParameter(newShaderBinder, gl.COMPILE_STATUS)) {
					throw new Error(gl.getShaderInfoLog(newShaderBinder) || '');
				}
			}
		
			return shaderBinder;
		};

		const vertexShaderBinder = compileShader(gl.VERTEX_SHADER, vertexShader);
		const fragmentShaderBinder = compileShader(gl.FRAGMENT_SHADER, fragmentShader);

		// creating the program
		const programBinder = gl.createProgram();
		if (programBinder === null) {
			throw new Error('Could not create program.');
		}

		gl.attachShader(programBinder, vertexShaderBinder);
		gl.attachShader(programBinder, fragmentShaderBinder);
		gl.linkProgram(programBinder);

		// check the link status
		if (!gl.getProgramParameter(programBinder, gl.LINK_STATUS)) {
			// something went wrong with the link
			gl.deleteProgram(programBinder);
			throw new Error('Error linking the program:' + gl.getProgramInfoLog(programBinder));
		}

		gl.useProgram(programBinder);

		// binding shader properties
		
		// uniforms -- they come from both shaders
		this._bindUniforms(gl, programBinder, vertexShader.textures, vertexShader.uniforms);
		this._bindUniforms(gl, programBinder, fragmentShader.textures, fragmentShader.uniforms);

		// vpMatrix always exists
		const uVPMatrixUniform = gl.getUniformLocation(programBinder, 'uVPMatrix');
		if (uVPMatrixUniform !== null) {
			this.uniforms.uVPMatrix = uVPMatrixUniform;
		}

		// attributes -- they only come from the vertex shader
		const attributes = vertexShader.attributes;
		for (let a = 0; a < attributes.length; a += 1) {
			const attribute = attributes[a];
			this.attributes[attribute] = gl.getAttribLocation(programBinder, attribute);
		}

		this.binder = programBinder;
		this.lastAttributeIndex = Object.keys(this.attributes).length - 1;
		this.lastTextures = [];

		// TODO: check program with glValidateProgram ?
	}

	_bindUniforms (gl: WebGLRenderingContext, programBinder: WebGLProgram, textures: Array<string>, uniforms: Array<string>) {
		for (let t = 0; t < textures.length; t += 1) {
			const texture = textures[t];
			if (this.textures[texture] === undefined) {
				gl.uniform1i(gl.getUniformLocation(programBinder, texture), t);
				this.textures[t] = gl['TEXTURE' + t];
			}
		}

		for (let u = 0; u < uniforms.length; u += 1) {
			const uniform = uniforms[u];
			if (this.uniforms[uniform] === undefined) {
				const uniformLocation = gl.getUniformLocation(programBinder, uniform);
				if (uniformLocation !== null) {
					this.uniforms[uniform] = uniformLocation;
				}
			}
		}
	}
}

export class Shader {
	private _vertexShader: VertexShader;
	private _fragmentShader: FragmentShader;

	private _compiledPrograms: Array<ShaderProgram>;
	program: ShaderProgram;

	constructor (vertexShader: VertexShader, fragmentShader: FragmentShader) {
		this._vertexShader = vertexShader;
		this._fragmentShader = fragmentShader;
		this._compiledPrograms = [];
		// @ts-ignore: not sure how to do otherwise...
		this.program = null;
	}

	compile (renderer: Renderer) {
		const rendererID = renderer.id;
		let program = this._compiledPrograms[rendererID];
		if (program === undefined) {
			this._compiledPrograms[rendererID] = program = new ShaderProgram(renderer.gl, rendererID, this._vertexShader, this._fragmentShader/*, this._render*/);
		}

		// saving for optimization purpose
		// should be possible to achieve the same by accessing shader.compiledPrograms[renderer.id]
		// but simple and fast to access through shader.program
		this.program = program;
	}

}

const dummyVertexShader = new VertexShader('', [], []);
const dummyFragmentShader = new FragmentShader('', [], [])
export const dummyShader = new Shader(dummyVertexShader, dummyFragmentShader);


export class BatchShader {
	shader: Shader;
 	render: renderMethod

	constructor (vertexShader: VertexShader, fragmentShader: FragmentShader, render: renderMethod) {
		this.shader = new Shader(vertexShader, fragmentShader);
		this.render = render;
	}
}

export const dummyBatchShader = new BatchShader(dummyVertexShader, dummyFragmentShader, () => {});

